import React from 'react';
import { useDatasourceId, useQueryId, useSeriesId } from '@/hooks';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@/api/baseQuery';
import {
  setFullResultsLoaded,
  setQueryExecutionTime,
  startQueryTimer,
} from '@/features/query-builder/queryBuilderSlice';
import { UUID } from '@/types';

export const PREPARE_TMP_SAVED_QUERY = 'prepareTmpSavedQuery';
export const GET_FULL_QUERY_RESULTS = 'getFullQueryResults';

export const fullQueryResultsApiSlice = createApi({
  reducerPath: 'fullQueryResultsApiSlice',
  baseQuery,
  endpoints: (builder) => ({
    [PREPARE_TMP_SAVED_QUERY]: builder.mutation<
      { seriesId: UUID },
      { datasourceId: UUID; queryId?: UUID }
    >({
      query: ({ datasourceId, queryId }) => ({
        url: `datasources/${datasourceId}/full-query-results/${queryId}/prepare`,
        method: 'POST',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(startQueryTimer());
        dispatch(setFullResultsLoaded(false));

        await queryFulfilled;
      },
    }),
    [GET_FULL_QUERY_RESULTS]: builder.query<any, any>({
      query: ({ datasourceId, seriesId }) =>
        `datasources/${datasourceId}/full-query-results/${seriesId}/load`,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const value = await queryFulfilled;
        if (value.data?.ready) {
          dispatch(setQueryExecutionTime(Date.now()));
          dispatch(setFullResultsLoaded(true));
        }
      },
    }),
  }),
});

const usePrepareSavedQuery = () => {
  const seriesIdParam = useSeriesId();
  const [seriesId, setSeriesId] = React.useState<Maybe<UUID>>();

  return [
    () => setSeriesId(seriesIdParam),
    seriesId ? { data: { seriesId } } : {},
  ] as const;
};

export const useLoadMoreResults = () => {
  const datasourceId = useDatasourceId();
  const queryId = useQueryId();
  const savedQueryResult = usePrepareSavedQuery();
  const adHocQueryResult = usePrepareTmpSavedQueryMutation();

  const [prepare, { data }] = queryId ? adHocQueryResult : savedQueryResult;

  const seriesId = data?.seriesId;

  return [() => prepare({ datasourceId, queryId }), seriesId] as const;
};

export const { usePrepareTmpSavedQueryMutation, useGetFullQueryResultsQuery } =
  fullQueryResultsApiSlice;
